import React, {useContext, useEffect, useLayoutEffect, useRef, useState, Suspense, lazy} from 'react';
import {BrowserRouter} from "react-router-dom";
import AppRouter from "./AppRouter";
import NavBar from "./components/layout/header/NavBar/NavBar";
import Navigation from "./components/layout/header/Navigation/Navigation";
import {observer} from "mobx-react-lite";
import {Context} from "./index";
import {checkAuth} from "./http/userApi";
import Loading from "./components/commons/Loading/Loading";
import {fetchDirectChatSessions} from "./http/directChatApi";
import {
    fetchArea,
    fetchBedroom,
    fetchBuilding,
    fetchFloor,
    fetchHeating,
    fetchParking,
    fetchRepair,
    fetchRoom
} from "./http/paramsApi";
import MobileBottomNav from "./components/layout/MobileBottomNav/MobileBottomNav/MobileBottomNav";
import CookieBannerManager from "./utils/CookieBannerManager/CookieBannerManager";
import {getOwnerSessions} from "./http/chatApi";
import './styles/base/global.css'
import GlobalChatNotifications from "./components/chats/GlobalChatNotifications/GlobalChatNotifications";
import useSiteVersionSync from "./components/hooks/useSiteVersionSync/useSiteVersionSync";
import {syncMessagesAppBadge} from "./utils/chat/syncMessagesAppBadge";
import ChatSocketBridge from "./components/realtime/ChatSocketBridge/ChatSocketBridge";
import {fetchRequestChatUnread} from "./http/requestChatApi";

const YMInitializerLazy = lazy(() =>
    import("react-yandex-metrika").then((m) => {
        const Comp = m.YMInitializer || m.default?.YMInitializer || m.default;
        if (!Comp) {
            // чтобы не падало в рантайме
            return { default: () => null };
        }
        return { default: Comp };
    })
);

const AppContent = ({ navBarRef, navigationRef, cookiesAccepted, handleAcceptCookies, metrikaOn }) => {
    useSiteVersionSync();

    return (
        <>
            {metrikaOn && (
                <Suspense fallback={null}>
                    <YMInitializerLazy accounts={[99134839]} options={{ webvisor: true }} />
                </Suspense>
            )}

            <NavBar ref={navBarRef} />
            <Navigation ref={navigationRef} />

            <GlobalChatNotifications />

            <AppRouter />
            <MobileBottomNav />

            <CookieBannerManager
                cookiesAccepted={cookiesAccepted}
                onAccept={handleAcceptCookies}
            />
        </>
    );
};

const App = observer(() => {
    // <<<--- 2. Добавляем chat в контекст
    const {
        user,
        add,
        helper,
        apartmentChat,
        directChat,
        requestChat,
        messages
    } = useContext(Context);
    const [isLoading, setIsLoading] = useState(true);

    const navBarRef = useRef(null);
    const navigationRef = useRef(null);

    const totalUnreadCount = messages.totalUnreadCount;

    const [cookiesAccepted, setCookiesAccepted] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem("cookie_consent") === "true";
        }
        return false;
    });

    const [metrikaOn, setMetrikaOn] = useState(false);

    const fetchRequestUnread = async () => {
        try {
            const data = await fetchRequestChatUnread();

            requestChat.setReadState({
                unreadCount: data.unreadCount || 0,
                lastReadMessageId: data.lastReadMessageId || 0,
                lastReadAt: data.lastReadAt || null
            });
        } catch (e) {
            console.error("Ошибка загрузки счётчика чата заявок:", e?.message || e);
        }
    };

    useEffect(() => {
        syncMessagesAppBadge(totalUnreadCount);
    }, [totalUnreadCount]);

    useEffect(() => {
        if (!cookiesAccepted) {
            setMetrikaOn(false);
            return;
        }

        const enable = () => {
            // чуть позже, чтобы не мешать LCP/TTI
            setTimeout(() => setMetrikaOn(true), 2500);
        };

        if (document.readyState === "complete") {
            enable();
            return;
        }

        window.addEventListener("load", enable, { once: true });
        return () => window.removeEventListener("load", enable);
    }, [cookiesAccepted]);

    const handleAcceptCookies = () => {
        setCookiesAccepted(true);
    }

    useEffect(() => {
        helper.setCookiesAccepted(cookiesAccepted);
    }, [cookiesAccepted, helper]);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        // Важно: должен совпадать с файлом в /public
        navigator.serviceWorker
            .register("/sw.js")
            .then((reg) => console.log("✅ SW registered:", reg.scope))
            .catch((err) => console.error("❌ SW register error:", err));
    }, []);

    const initializeUser = async () => {
        try {
            const userData = await checkAuth();
            user.setIsAuth(true);
            user.setUserData(userData);
            return true;
        } catch (e) {
            user.setIsAuth(false);
            localStorage.removeItem("token");
            document.cookie = "refreshToken=; Max-Age=0; path=/";
            return false;
        }
    };

    const fetchChats = async () => {
        apartmentChat.setIsLoading(true);

        try {
            const data = await getOwnerSessions();
            apartmentChat.setSessions(data.sessions || []);
        } catch (e) {
            console.error(e.message);
        } finally {
            apartmentChat.setIsLoading(false);
        }
    };

    const fetchDirectChats = async () => {
        try {
            const data = await fetchDirectChatSessions({
                page: 1,
                limit: 50
            });

            directChat.setSessions(data.sessions || []);
        } catch (e) {
            console.error("Ошибка загрузки личных чатов:", e.message);
        }
    };

    useEffect(() => {
        if (user.isAuth) {
            fetchChats();
            fetchDirectChats();
            fetchRequestUnread();
        } else {
            messages.clearAllRuntimeState();
        }
    }, [user.isAuth]);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                const checkPromise = initializeUser();

                const [
                    bedrooms, buildings, floors, rooms,
                    heatings, repairs, parkings, areas
                ] = await Promise.all([
                    fetchBedroom(), fetchBuilding(), fetchFloor(), fetchRoom(),
                    fetchHeating(), fetchRepair(), fetchParking(), fetchArea()
                ]);

                add.setBedrooms(bedrooms);
                add.setBuildings(buildings);
                add.setFloors(floors);
                add.setRooms(rooms);
                add.setHeatings(heatings);
                add.setRepairs(repairs);
                add.setParkings(parkings);
                add.setAreas(areas);

                const isAuthed = await checkPromise;

                const hasGuestSession = localStorage.getItem('guestSessionId');

                if (isAuthed || hasGuestSession) {
                    await fetchChats();

                    if (isAuthed) {
                        await fetchDirectChats();
                        await fetchRequestUnread();
                    }
                }
            } catch (error) {
                console.error("Ошибка инициализации приложения:", error);
            } finally {
                user.setIsLoaded(true);
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const isAuthed = await initializeUser();

                if (isAuthed) {
                    await fetchChats();
                    await fetchDirectChats();
                    await fetchRequestUnread();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useLayoutEffect(() => {
        if (isLoading) return;

        const navEl = navBarRef.current;
        const nav2El = navigationRef.current;
        if (!navEl || !nav2El) return;

        const update = () => {
            helper.setNavBarHeight(navEl.getBoundingClientRect().height || 0);
            helper.setNavigationHeight(nav2El.getBoundingClientRect().height || 0);
        };

        update(); // сразу

        const ro = new ResizeObserver(update);
        ro.observe(navEl);
        ro.observe(nav2El);

        window.addEventListener("resize", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [isLoading, helper]);

    if (isLoading) {
        return (
            <Loading/>
        );
    }

    return (
        <BrowserRouter>
            <ChatSocketBridge />

            <AppContent
                navBarRef={navBarRef}
                navigationRef={navigationRef}
                cookiesAccepted={cookiesAccepted}
                handleAcceptCookies={handleAcceptCookies}
                metrikaOn={metrikaOn}
            />
        </BrowserRouter>
    );
});

export default App;